/**
 * Geometry for OSD annotations. Stored `new_bounds` use OL coordinates (image pixels, y up from
 * the bottom); everything here works in those coordinates unless a name says "screen".
 *   polygon: [[[x, y], ...], ...rings]   line: [[x, y], ...]   circle: [[cx, cy], radius]
 */
import {AnnotationsConstants} from '../../../../utils/const';

export const parseBounds = (annotation) => {
    if (!annotation?.new_bounds) return null;
    try {
        return JSON.parse(annotation.new_bounds);
    } catch (e) {
        return null;
    }
};

const segmentLength = ([x1, y1], [x2, y2]) => Math.hypot(x2 - x1, y2 - y1);

export const pathLength = (points) => {
    let length = 0;
    for (let i = 1; i < points.length; i++) length += segmentLength(points[i - 1], points[i]);
    return length;
};

/** Shoelace area of an open or closed ring. */
export const ringArea = (ring) => {
    let area = 0;
    for (let i = 0; i < ring.length; i++) {
        const [x1, y1] = ring[i];
        const [x2, y2] = ring[(i + 1) % ring.length];
        area += x1 * y2 - x2 * y1;
    }
    return Math.abs(area / 2);
};

export const closeRing = (ring) => {
    if (ring.length < 2) return ring;
    const [fx, fy] = ring[0];
    const [lx, ly] = ring[ring.length - 1];
    return fx === lx && fy === ly ? ring : [...ring, ring[0]];
};

/** [minX, minY, maxX, maxY] of an annotation, or null. */
export const getAnnotationExtent = (annotation) => {
    const bounds = parseBounds(annotation);
    if (!bounds) return null;
    if (annotation.shape === AnnotationsConstants.CIRCLE) {
        const [[cx, cy], r] = bounds;
        return [cx - r, cy - r, cx + r, cy + r];
    }
    const points = annotation.shape === AnnotationsConstants.POLYGON ? bounds.flat() : bounds;
    if (!points.length) return null;
    const xs = points.map(p => p[0]);
    const ys = points.map(p => p[1]);
    return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
};

export const extentCenter = ([minX, minY, maxX, maxY]) => [(minX + maxX) / 2, (minY + maxY) / 2];

/** Even-odd test over all rings, like OL `Polygon.intersectsCoordinate`. */
export const polygonContains = (rings, [x, y]) => {
    let inside = false;
    for (const ring of rings) {
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const [xi, yi] = ring[i];
            const [xj, yj] = ring[j];
            if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
        }
    }
    return inside;
};

/** Same check as OL draw tools: the new annotation's extent centre must lie inside the ROI. */
export const annotationContainsPoint = (annotation, point) => {
    const bounds = parseBounds(annotation);
    if (!bounds) return false;
    if (annotation.shape === AnnotationsConstants.CIRCLE) {
        const [[cx, cy], r] = bounds;
        return Math.hypot(point[0] - cx, point[1] - cy) <= r;
    }
    if (annotation.shape === AnnotationsConstants.POLYGON) return polygonContains(bounds, point);
    return false;
};

/**
 * Point at half the path length and the direction of the segment it falls on, for OL-style
 * `placement: 'line'` labels. Works on screen points.
 */
export const pathMidpoint = (points) => {
    if (!points || points.length < 2) return null;
    const half = pathLength(points) / 2;
    let walked = 0;
    for (let i = 1; i < points.length; i++) {
        const length = segmentLength(points[i - 1], points[i]);
        if (walked + length >= half && length > 0) {
            const t = (half - walked) / length;
            const [x1, y1] = points[i - 1];
            const [x2, y2] = points[i];
            return {x: x1 + t * (x2 - x1), y: y1 + t * (y2 - y1), angle: Math.atan2(y2 - y1, x2 - x1)};
        }
        walked += length;
    }
    const [x, y] = points[points.length - 1];
    return {x, y, angle: 0};
};

/** Keeps label text upright, as OL does for line placement. */
export const uprightAngle = (angle) => {
    if (angle > Math.PI / 2) return angle - Math.PI;
    if (angle < -Math.PI / 2) return angle + Math.PI;
    return angle;
};
